// src/hooks/useCalendarModals.ts
// Calendar Modal 管理 Hook - 統一管理 Event 和 TimeBox 的 Modal 狀態和操作

import { useState, useCallback } from 'react';
import { eventBus, EVENTS } from '../../../utils/eventBus';

interface UseCalendarModalsOptions {
  onRefresh: () => Promise<void>;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

export function useCalendarModals(options: UseCalendarModalsOptions) {
  const { onRefresh, onSuccess } = options;

  // ============================================
  // TimeBox Detail 狀態（查看/編輯專用）
  // ============================================
  const [showTimeBoxDetail, setShowTimeBoxDetail] = useState(false);
  const [selectedTimeBoxId, setSelectedTimeBoxId] = useState<string | null>(null);

  // ============================================
  // Event Detail 狀態（查看/編輯專用）
  // ============================================
  const [showEventDetail, setShowEventDetail] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  // ============================================
  // QuickCreate Modal 狀態
  // ============================================
  const [showQuickCreateModal, setShowQuickCreateModal] = useState(false);
  const [quickCreateStartTime, setQuickCreateStartTime] = useState<Date | undefined>(undefined);
  const [quickCreateEndTime, setQuickCreateEndTime] = useState<Date | undefined>(undefined);

  // ============================================
  // TimeBox Detail Handlers（查看/編輯專用）
  // ============================================

  // 開啟 TimeBox Detail
  const openTimeBoxDetail = useCallback((timeBoxId: string) => {
    setSelectedTimeBoxId(timeBoxId);
    setShowTimeBoxDetail(true);
  }, []);

  // 關閉 TimeBox Detail
  const closeTimeBoxDetail = useCallback(() => {
    setShowTimeBoxDetail(false);
    setSelectedTimeBoxId(null);
  }, []);

  // ============================================
  // Event Detail Handlers（查看/編輯專用）
  // ============================================

  // 開啟 Event Detail（ID-based）
  const openEventDetail = useCallback((eventId: string) => {
    setSelectedEventId(eventId);
    setShowEventDetail(true);
  }, []);

  // 關閉 Event Detail
  const closeEventDetail = useCallback(() => {
    setShowEventDetail(false);
    setSelectedEventId(null);
  }, []);

  // ============================================
  // QuickCreate Modal Handlers
  // ============================================

  // 開啟 QuickCreate Modal
  const openQuickCreateModal = useCallback((startTime?: Date, endTime?: Date) => {
    setQuickCreateStartTime(startTime);
    setQuickCreateEndTime(endTime);
    setShowQuickCreateModal(true);
    eventBus.publish(EVENTS.UI.QUICK_CREATE_MODAL_OPENED);
  }, []);

  // 關閉 QuickCreate Modal
  const closeQuickCreateModal = useCallback(() => {
    setShowQuickCreateModal(false);
    setQuickCreateStartTime(undefined);
    setQuickCreateEndTime(undefined);
  }, []);

  // QuickCreate 成功回調
  const handleQuickCreateSuccess = useCallback(async () => {
    closeQuickCreateModal();
    onSuccess('Schedule item created successfully');
    await onRefresh();
  }, [closeQuickCreateModal, onSuccess, onRefresh]);

  // ============================================
  // Return
  // ============================================

  return {
    // TimeBox Detail (查看/編輯專用)
    timeBoxDetail: {
      isOpen: showTimeBoxDetail,
      selectedId: selectedTimeBoxId,
      open: openTimeBoxDetail,
      close: closeTimeBoxDetail,
    },
    // Event Detail (查看/編輯專用)
    eventDetail: {
      isOpen: showEventDetail,
      selectedId: selectedEventId,
      open: openEventDetail,
      close: closeEventDetail,
    },
    // QuickCreate Modal
    quickCreateModal: {
      isOpen: showQuickCreateModal,
      startTime: quickCreateStartTime,
      endTime: quickCreateEndTime,
      open: openQuickCreateModal,
      close: closeQuickCreateModal,
      onSuccess: handleQuickCreateSuccess,
    },
  };
}
